import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, TextField, Select, MenuItem, InputLabel,
  FormControl, Snackbar, Alert, CircularProgress, InputAdornment,
  Tooltip, IconButton, Divider, useMediaQuery, Stack, Pagination,
  FormGroup, FormControlLabel, Radio, RadioGroup
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Plus, X, Search, Trash2, Building, AlarmClock, Calendar, User
} from 'lucide-react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';

const TIMEZONE = 'America/New_York';

const ClocksPage = () => {
  // States (kept as in the user's file)
  const [clockEntries, setClockEntries] = useState([]);
  const [individualLogs, setIndividualLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLogDataContext, setEditLogDataContext] = useState(null);
  const [currentEditingLogChanges, setCurrentEditingLogChanges] = useState({});
  const [newLogData, setNewLogData] = useState({ type: '', timestamp_time: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [isGeneralAddMode, setIsGeneralAddMode] = useState(false);
  const [generalNewLogData, setGeneralNewLogData] = useState({
    employee_id: '', hotel_id: '', type: '',
    timestamp_date: new Date(), timestamp_time: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // UI toggles
  const [showBreakColumns, setShowBreakColumns] = useState(false);   // Break Out / Break In columns + break types
  const [showMeetingColumns, setShowMeetingColumns] = useState(false); // ✅ Meeting columns in SAME table

  // NEW: active-only employees for the Add dialog + search
  const [activeEmployees, setActiveEmployees] = useState([]); // array like [id, name, ..., status]
  const [empSearch, setEmpSearch] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchTimeoutRef = useRef(null);
  const { refreshKey } = useRefresh();

  const showSnackbar = (message, severity = 'success') => { setSnackbar({ open: true, message, severity }); };

  // Fetch employees and hotels (kept)
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [empRes, hotelRes] = await Promise.all([api.get('/employees'), api.get('/admin/hotels')]);
        const mappedEmployees = (empRes.data || []).map(emp => ({ id: emp[0], name: emp[1] }));
        setEmployees([{ id: '', name: 'All Employees' }, ...mappedEmployees]);
        const activeHotels = (hotelRes.data || []).filter(hotel => hotel.status === 'active');
        setHotels([{ id: '', name: 'All Hotels' }, ...activeHotels]);
      } catch (error) {
        console.error("Dropdown error:", error);
        showSnackbar("Error loading options", "error");
      }
    };
    fetchDropdownData();
  }, []);

  // Function to fetch data with pagination and search
  const fetchAggregatedLogs = useCallback(async (currentPage, currentSearch, showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const currentRowsPerPage = 50;
      setRowsPerPage(currentRowsPerPage);
      const params = { page: currentPage + 1, per_page: currentRowsPerPage, search: currentSearch.trim() };
      const response = await api.get('/admin/clocks', { params });
      setClockEntries(response.data?.logs || []);
      const paginationData = response.data?.pagination;
      const fetchedTotalRows = paginationData?.total_rows || 0;
      setTotalRows(fetchedTotalRows);
      setTotalPages(paginationData?.total_pages || Math.ceil(fetchedTotalRows / currentRowsPerPage));
    } catch (error) {
      console.error('Error fetching records:', error);
      showSnackbar('Error loading records', 'error');
      setClockEntries([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Load data when page changes
  useEffect(() => {
    fetchAggregatedLogs(page, search);
  }, [page, fetchAggregatedLogs]);

  // Search debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      if (search !== '') {
        setPage(0);
        fetchAggregatedLogs(0, search);
      } else {
        fetchAggregatedLogs(page, search);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search]);

  // Close Dialog + reset
  const handleDialogClose = () => {
    setDialogOpen(false);
    setIsGeneralAddMode(false);
    setTimeout(() => {
      setEditLogDataContext(null);
      setIndividualLogs([]);
      setCurrentEditingLogChanges({});
      setGeneralNewLogData({ employee_id: '', hotel_id: '', type: '', timestamp_date: new Date(), timestamp_time: '' });
      setNewLogData({ type: '', timestamp_time: '' });
      setActiveEmployees([]);
      setEmpSearch('');
    }, 150);
  };

  // Open edit dialog
  const handleOpenEditDialog = useCallback(async (rowData) => {
    if (!rowData?.log_date || !rowData?.employee_id || !rowData?.hotel_id) {
      showSnackbar("Incomplete data.", "error");
      return;
    }
    setLoadingDialog(true);
    setIsGeneralAddMode(false);
    setEditLogDataContext(rowData);
    setCurrentEditingLogChanges({});
    setNewLogData({ type: '', timestamp_time: '' });
    setIndividualLogs([]);
    try {
      const params = { date: rowData.log_date, employee_id: rowData.employee_id, hotel_id: rowData.hotel_id };
      const response = await api.get('/admin/clocks/details', { params });
      const sortedLogs = (response.data || []).sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      setIndividualLogs(sortedLogs);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error fetching log details:", error);
      showSnackbar("Error loading details", "error");
    } finally {
      setLoadingDialog(false);
    }
  }, []);

  // Helper: format API timestamp -> datetime-local input
  const formatTimestampForInput = (apiTimestamp) => {
    if (!apiTimestamp) return '';
    try {
      const dateObj = parseISO(apiTimestamp);
      const zonedTime = toZonedTime(dateObj, TIMEZONE);
      return format(zonedTime, "yyyy-MM-dd'T'HH:mm");
    } catch (e) {
      console.error("Error formatting API->Input timestamp:", apiTimestamp, e);
      return '';
    }
  };

  // Save individual change (onBlur)
  const handleSaveIndividualLogChange = async (logId) => {
    const newValueString = currentEditingLogChanges[logId];
    if (newValueString === undefined) return;

    if (!newValueString || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(newValueString)) {
      showSnackbar("Invalid date/time format. Use YYYY-MM-DDTHH:MM.", "warning");
      return;
    }

    setLoadingDialog(true);
    try {
      await api.put(`/admin/clocks/${logId}`, { timestamp: newValueString });
      showSnackbar("Time updated successfully");
      fetchAggregatedLogs(page, search, false);
      setCurrentEditingLogChanges(prev => {
        const newState = { ...prev };
        delete newState[logId];
        return newState;
      });
      if (editLogDataContext) await handleOpenEditDialog(editLogDataContext);
    } catch (error) {
      console.error("Error updating log:", error);
      const errorMsg = error.response?.data?.msg || "Error updating log";
      showSnackbar(errorMsg, "error");
      if (editLogDataContext) await handleOpenEditDialog(editLogDataContext);
    } finally {
      setLoadingDialog(false);
    }
  };

  // Delete individual log
  const handleDeleteIndividualLog = async (logId) => {
    if (!window.confirm("Delete this record?")) return;
    setLoadingDialog(true);
    try {
      await api.delete(`/admin/clocks/${logId}`);
      showSnackbar("Record deleted");
      setIndividualLogs(prev => prev.filter(log => log.id !== logId));
      fetchAggregatedLogs(page, search, false);
      if (individualLogs.length === 1) handleDialogClose();
    } catch (error) {
      console.error("Error deleting:", error);
      showSnackbar(error.response?.data?.msg || "Error deleting record", "error");
    } finally {
      setLoadingDialog(false);
    }
  };

  const handleIndividualLogInputChange = (logId, newValue) => {
    setCurrentEditingLogChanges(prev => ({ ...prev, [logId]: newValue }));
  };

  const handleNewLogInputChange = (e) => {
    const { name, value } = e.target;
    setNewLogData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddNewLogInDialog = async () => {
    const { type, timestamp_time } = newLogData;
    const { employee_id, hotel_id, log_date } = editLogDataContext;
    if (!type || !timestamp_time) { showSnackbar("Type and Time are required", "warning"); return; }
    if (!/^[0-2]?[0-9]:[0-5][0-9]$/.test(timestamp_time)) { showSnackbar("Invalid time format (HH:MM)", "warning"); return; }

    const combinedTimestampStr = `${log_date}T${timestamp_time}`;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(combinedTimestampStr)) {
      showSnackbar("Invalid final date/time format.", "error");
      return;
    }

    setLoadingDialog(true);
    try {
      await api.post('/admin/clocks', { employee_id, hotel_id, type, timestamp: combinedTimestampStr });
      showSnackbar("New record added");
      setNewLogData({ type: '', timestamp_time: '' });
      fetchAggregatedLogs(page, search, false);
      if (editLogDataContext) await handleOpenEditDialog(editLogDataContext);
    } catch (error) {
      console.error("Error adding (contextual):", error);
      const errorMsg = error.response?.data?.msg || "Error adding record";
      showSnackbar(errorMsg, "error");
    } finally {
      setLoadingDialog(false);
    }
  };

  // Grouped + filtered active employees for selection
  const filteredAndGroupedActive = useMemo(() => {
    const byName = (a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: 'base' });
    const filtered = (activeEmployees || [])
      .filter((e) => {
        if (!empSearch.trim()) return true;
        return String(e[1]).toLowerCase().includes(empSearch.trim().toLowerCase());
      })
      .sort(byName);

    const groups = {};
    filtered.forEach((emp) => {
      const name = String(emp[1] || '');
      const letter = name.charAt(0).toUpperCase();
      const key = /[A-Z]/.test(letter) ? letter : '#';
      if (!groups[key]) groups[key] = [];
      groups[key].push(emp);
    });

    const orderedKeys = Object.keys(groups).sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });

    return { groups, orderedKeys };
  }, [activeEmployees, empSearch]);

  const handleOpenGeneralAddDialog = async () => {
    setIsGeneralAddMode(true);
    setEditLogDataContext(null);
    setIndividualLogs([]);

    const todayInNY = toZonedTime(new Date(), TIMEZONE);
    setGeneralNewLogData({ employee_id: '', hotel_id: '', type: '', timestamp_date: todayInNY, timestamp_time: '' });
    setEmpSearch('');

    setLoadingDialog(true);
    try {
      const empRes = await api.get('/employees');
      const ativos = (empRes.data || []).filter((emp) => emp[6] === 'active');
      const sorted = [...ativos].sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: 'base' }));
      setActiveEmployees(sorted);
    } catch (err) {
      console.error('Error loading employees:', err);
      setActiveEmployees([]);
      showSnackbar('Error loading employees', 'error');
    } finally {
      setLoadingDialog(false);
      setDialogOpen(true);
    }
  };

  const handleGeneralNewLogInputChange = (e) => {
    const { name, value } = e.target;
    setGeneralNewLogData(prev => ({ ...prev, [name]: value }));
  };

  const handleGeneralNewLogDateChange = (newValue) => {
    setGeneralNewLogData(prev => ({ ...prev, timestamp_date: newValue || new Date() }));
  };

  const handleSaveGeneralAddLog = async () => {
    const { employee_id, hotel_id, type, timestamp_date, timestamp_time } = generalNewLogData;
    if (!employee_id || !hotel_id || !type || !timestamp_date || !timestamp_time) { showSnackbar("All fields are required", "warning"); return; }
    if (!isValid(timestamp_date)) { showSnackbar("Invalid date", "warning"); return; }
    if (!/^[0-2]?[0-9]:[0-5][0-9]$/.test(timestamp_time)) { showSnackbar("Invalid time format (HH:MM)", "warning"); return; }

    const dateString = format(timestamp_date, 'yyyy-MM-dd');
    const combinedTimestampStr = `${dateString}T${timestamp_time}`;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(combinedTimestampStr)) { showSnackbar("Invalid final date/time format.", "error"); return; }

    setLoadingDialog(true);
    try {
      await api.post('/admin/clocks', { employee_id, hotel_id, type, timestamp: combinedTimestampStr });
      showSnackbar("New record added");
      handleDialogClose();
      setPage(0);
      fetchAggregatedLogs(0, '', true);
    } catch (error) {
      console.error("General add error:", error);
      const errorMsg = error.response?.data?.msg || "Error adding record";
      showSnackbar(errorMsg, "error");
    } finally {
      setLoadingDialog(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  // Refresh context
  useEffect(() => {
    fetchAggregatedLogs(page, search, true);
  }, [refreshKey]);

  // dynamic colSpan for loading/empty row
  const mainTableColSpan =
    4 + // Date, Employee, Hotel, In
    (showBreakColumns ? 2 : 0) + // Break Out, Break In
    1 + // Out
    (showMeetingColumns ? 2 : 0); // Meeting Start, Meeting End

  return (
    <Box p={1}>
      {/* Top Bar */}
      <Box
        sx={{
          pb: 1,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 1,
          maxWidth: 1000,
          margin: '0 auto',
        }}
      >
        <TextField
          size='small'
          placeholder="Search employee / hotel / date (YYYY-MM-DD)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          sx={{
            flex: 7,
            backgroundColor: '#fff',
            borderRadius: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            width: { xs: '100%', sm: 'auto' },
            '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#757575" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearch('')} size="small" edge="end">
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={handleOpenGeneralAddDialog}
          sx={{
            flex: 3,
            height: '40px',
            whiteSpace: 'nowrap',
            width: { xs: '100%', sm: 'auto' },
            background: 'linear-gradient(to right, #1976d2, #0d47a1)',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': { background: 'linear-gradient(to right, #1565c0, #003c8f)' },
          }}
          disabled={loading}
        >
          Add Record
        </Button>

        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant={showBreakColumns ? "contained" : "outlined"}
            onClick={() => setShowBreakColumns((v) => !v)}
            sx={{
              height: '40px',
              borderRadius: 5,
              fontWeight: 800,
              whiteSpace: 'nowrap',
              px: 1.6,
              ...(showBreakColumns
                ? { background: 'linear-gradient(90deg, #f59e0b, #ef4444)', color: '#fff', border: 'none' }
                : { borderColor: 'rgba(0,0,0,0.15)', color: '#444' }),
            }}
          >
            Breaks
          </Button>

          {/* ✅ Meetings agora só mostra colunas na mesma tabela */}
          <Button
            variant={showMeetingColumns ? "contained" : "outlined"}
            onClick={() => setShowMeetingColumns((v) => !v)}
            sx={{
              height: '40px',
              borderRadius: 5,
              fontWeight: 800,
              whiteSpace: 'nowrap',
              px: 1.6,
              ...(showMeetingColumns
                ? { background: 'linear-gradient(90deg, #ec4899, #a855f7)', color: '#fff', border: 'none' }
                : { borderColor: 'rgba(0,0,0,0.15)', color: '#444' }),
            }}
          >
            Meetings
          </Button>
        </Box>
      </Box>

      {/* Main table */}
      <Box
        sx={{
          overflowX: 'auto',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          maxWidth: 1000,
          margin: '0 auto',
          width: '100%',
        }}
      >
        <Paper sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: '#f0f0f0', fontWeight: 'bold', whiteSpace: 'nowrap' } }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Hotel</TableCell>
                  <TableCell align="center">In</TableCell>
                  {showBreakColumns && <TableCell align="center">Break Out</TableCell>}
                  {showBreakColumns && <TableCell align="center">Break In</TableCell>}
                  <TableCell align="center">Out</TableCell>

                  {/* ✅ Meeting columns in the same table */}
                  {showMeetingColumns && <TableCell align="center">Meeting Start</TableCell>}
                  {showMeetingColumns && <TableCell align="center">Meeting End</TableCell>}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && clockEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={mainTableColSpan} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !loading && clockEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={mainTableColSpan} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No records found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clockEntries.map((entry) => (
                    <TableRow
                      hover
                      key={`${entry.log_date}-${entry.employee_id}-${entry.hotel_id}`}
                      onClick={() => handleOpenEditDialog(entry)}
                      sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' } }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {entry.log_date ? format(parseISO(entry.log_date), 'MM/dd/yy') : '-'}
                      </TableCell>
                      <TableCell>{entry.employee_name}</TableCell>
                      <TableCell>{entry.hotel_name}</TableCell>
                      <TableCell align="center">{entry.clock_in || '--:--'}</TableCell>
                      {showBreakColumns && <TableCell align="center">{entry.break_out || '--:--'}</TableCell>}
                      {showBreakColumns && <TableCell align="center">{entry.break_in || '--:--'}</TableCell>}
                      <TableCell align="center">{entry.clock_out || '--:--'}</TableCell>

                      {/* ✅ Meeting values from /admin/clocks */}
                      {showMeetingColumns && <TableCell align="center">{entry.meeting_start || '--:--'}</TableCell>}
                      {showMeetingColumns && <TableCell align="center">{entry.meeting_end || '--:--'}</TableCell>}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Pagination */}
      {!loading && totalRows > 0 && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          mt={2}
          px={1}
          maxWidth="1000px"
          mx="auto"
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {`Records ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRows)} of ${totalRows}`}
          </Typography>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            size={isMobile ? "small" : "medium"}
          />
        </Stack>
      )}

      {/* Dialog (edit and general add) */}
      <Dialog
        open={dialogOpen || isGeneralAddMode}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlarmClock size={24} color="#1976d2" />
            <span>
              {isGeneralAddMode
                ? 'New Time Clock Record'
                : `${editLogDataContext?.employee_name || ''} | ${editLogDataContext?.hotel_name || ''} (${editLogDataContext?.log_date ? format(parseISO(editLogDataContext.log_date), 'MM/dd/yyyy') : ''})`}
            </span>
          </Box>
          {loadingDialog && <CircularProgress size={24} />}
        </DialogTitle>

        <DialogContent dividers>
          {isGeneralAddMode ? (
            // GENERAL ADD FORM with grouped active employee picker
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1} sx={{ color: '#333' }}>
                New Clock Record
              </Typography>

              {/* EMPLOYEE PICKER */}
              <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 1.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                  <TextField
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    placeholder="Search employee..."
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={16} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Divider />

                <Box sx={{ maxHeight: 300, overflow: 'auto', p: 1.5 }}>
                  <RadioGroup
                    name="employee_select"
                    value={String(generalNewLogData.employee_id || '')}
                    onChange={(e) => setGeneralNewLogData((prev) => ({ ...prev, employee_id: Number(e.target.value) }))}
                  >
                    {filteredAndGroupedActive.orderedKeys.map((letter) => (
                      <Box key={letter} sx={{ mb: 1.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ px: 1.5, py: 0.5, mb: 1, fontWeight: 700, bgcolor: 'action.hover', borderRadius: 1 }}
                        >
                          {letter}
                        </Typography>
                        <FormGroup>
                          {filteredAndGroupedActive.groups[letter].map((emp) => (
                            <FormControlLabel
                              key={emp[0]}
                              value={String(emp[0])}
                              control={<Radio />}
                              label={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <User size={16} />
                                  <Typography sx={{ fontWeight: 500 }}>{emp[1]}</Typography>
                                </Box>
                              }
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    ))}
                    {filteredAndGroupedActive.orderedKeys.length === 0 && (
                      <Typography sx={{ color: 'text.secondary' }}>No employees found.</Typography>
                    )}
                  </RadioGroup>
                </Box>
              </Box>

              {/* HOTEL SELECT */}
              <FormControl fullWidth required>
                <InputLabel id="gen-add-hotel-label">Hotel</InputLabel>
                <Select
                  labelId="gen-add-hotel-label"
                  name="hotel_id"
                  value={generalNewLogData.hotel_id}
                  label="Hotel"
                  onChange={handleGeneralNewLogInputChange}
                  startAdornment={<InputAdornment position="start"><Building size={18} color="#666" /></InputAdornment>}
                >
                  <MenuItem value="" disabled><em>Select...</em></MenuItem>
                  {hotels.filter(h => h.id).map(h => (
                    <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* TYPE */}
              <FormControl fullWidth required>
                <InputLabel id="gen-add-type-label">Type</InputLabel>
                <Select
                  labelId="gen-add-type-label"
                  name="type"
                  value={generalNewLogData.type}
                  label="Type"
                  onChange={handleGeneralNewLogInputChange}
                  startAdornment={<InputAdornment position="start"><AlarmClock size={18} color="#666" /></InputAdornment>}
                >
                  <MenuItem value="" disabled><em>Select...</em></MenuItem>
                  <MenuItem value="clock_in">Clock In</MenuItem>
                  {showBreakColumns && <MenuItem value="break_out">Break Out</MenuItem>}
                  {showBreakColumns && <MenuItem value="break_in">Break In</MenuItem>}
                  <MenuItem value="clock_out">Clock Out</MenuItem>
                </Select>
              </FormControl>

              {/* DATE */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date"
                  value={generalNewLogData.timestamp_date}
                  onChange={handleGeneralNewLogDateChange}
                  maxDate={new Date()}
                  format="MM/dd/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} color="#666" />
                          </InputAdornment>
                        ),
                      },
                    }
                  }}
                />
              </LocalizationProvider>

              {/* TIME */}
              <TextField
                label="Time (HH:MM)"
                type="time"
                name="timestamp_time"
                value={generalNewLogData.timestamp_time}
                onChange={handleGeneralNewLogInputChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AlarmClock size={18} color="#666" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                onClick={handleSaveGeneralAddLog}
                disabled={loadingDialog}
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  fontWeight: 900,
                  background: 'linear-gradient(90deg, #22c55e, #16a34a)',
                  '&:hover': { background: 'linear-gradient(90deg, #16a34a, #15803d)' }
                }}
              >
                Save
              </Button>
            </Box>
          ) : loadingDialog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            // EDIT CONTENT
            <>
              <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
                Existing Records
              </Typography>

              {individualLogs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ddd', mb: 3 }}>
                  <Typography color="text.secondary">No records found</Typography>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f0f0f0' } }}>
                          <TableCell>Type</TableCell>
                          <TableCell>Date and Time</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {individualLogs.map(log => {
                          const displayValue = formatTimestampForInput(log.timestamp);
                          const inputValue = currentEditingLogChanges[log.id] !== undefined
                            ? currentEditingLogChanges[log.id]
                            : displayValue;

                          return (
                            <TableRow key={log.id} sx={{ bgcolor: 'white' }}>
                              <TableCell sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}>
                                {log.type?.replace('_', ' ') || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="datetime-local"
                                  value={inputValue}
                                  onChange={e => handleIndividualLogInputChange(log.id, e.target.value)}
                                  onBlur={() => handleSaveIndividualLogChange(log.id)}
                                  size="small"
                                  sx={{ minWidth: 195 }}
                                  InputLabelProps={{ shrink: true }}
                                  disabled={loadingDialog}
                                  inputProps={{ step: 60 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Delete">
                                  <span>
                                    <IconButton
                                      onClick={() => handleDeleteIndividualLog(log.id)}
                                      size="small"
                                      disabled={loadingDialog}
                                      sx={{ color: '#ef4444' }}
                                    >
                                      <Trash2 size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" mb={1} sx={{ color: '#333' }}>
                Add New Record
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 140 }} size="small">
                  <InputLabel id="new-type-label">Type</InputLabel>
                  <Select
                    labelId="new-type-label"
                    name="type"
                    value={newLogData.type}
                    label="Type"
                    onChange={handleNewLogInputChange}
                  >
                    <MenuItem value=""><em>Select...</em></MenuItem>
                    <MenuItem value="clock_in">Clock In</MenuItem>
                    {showBreakColumns && <MenuItem value="break_out">Break Out</MenuItem>}
                    {showBreakColumns && <MenuItem value="break_in">Break In</MenuItem>}
                    <MenuItem value="clock_out">Clock Out</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Time"
                  type="time"
                  name="timestamp_time"
                  value={newLogData.timestamp_time}
                  onChange={handleNewLogInputChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                  sx={{ minWidth: 140 }}
                />

                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={handleAddNewLogInDialog}
                  disabled={loadingDialog}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    background: 'linear-gradient(90deg, #22c55e, #16a34a)',
                    '&:hover': { background: 'linear-gradient(90deg, #16a34a, #15803d)' }
                  }}
                >
                  Add
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClocksPage;
